import React from 'react';
import { useKeyboardManagerState } from '../../hooks/useKeyboardManagerState';
import { KeyboardShortcutState } from '../../redux/types';
import { StringWithMatches } from '../StringWithMatches';
import { groupBy } from '../../utils/groupBy';
import { useFullTextSearch } from '../../hooks/useFullTextSearch';
import { Conflict } from '../../redux/types';
import { Shortcut } from '../Shortcut';
import { Button } from '../../../../Components/Button';
import { MIcon } from '../../../../Components/Icon';
import { Dialog, DialogFooter } from '../../../../Components/Dialog';
import { StatelessAccordion } from 'baseui/accordion';
import { shortcutToString } from '../../utils/shortcutToString';

import { styled } from 'baseui';
import { borderStyle, paddingSize } from '../../../../Utilities/utils/Theming';
import { StyleObject } from 'styletron-standard';
import UpdateShortcut from './UpdateShortcuts';
import KSMFeedback from './KSMFeedback';
import { itemStyle, labelStyle, shortcutActionsStyle, shortcutsScrollableContainerStyle, unstyledListStyle } from '../styles/shortcuts';
import { useStyletron } from '../../../../Themes';
import { TGroupedItem, TView } from '../types';
import { CustomPanel } from '../components/CustomPanel';
import { Input } from '../../../../Components/Input';
import { useReactToPrint } from 'react-to-print';

const Aside = styled('aside', {
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    color: 'var(--epods--color-grey-950)',

    // TODO: this needs to be integrated with the header panels
    height: '100%',
});

export const CustomStatelessAccordion: React.FC<{
    expanded: React.Key[];
    setExpanded: (value: React.SetStateAction<React.Key[]>) => void;
}> = ({ children, expanded, setExpanded }) => {
    return (
        <StatelessAccordion
            accordion={false}
            expanded={expanded}
            onChange={({ key, expanded }) => {
                setExpanded(expanded);
            }}
            overrides={{
                Root: {
                    style: {
                        marginTop: 0,
                        marginBottom: 0,
                        marginLeft: 0,
                        marginRight: 0,
                    },
                },
                Content: {
                    style: {
                        ...borderStyle('none'),
                        ...paddingSize('0', '0', '0', '0'),
                        backgroundColor: 'none',
                    } as StyleObject,
                },
                Header: {
                    style: {
                        flexDirection: 'row-reverse',
                        justifyContent: 'flex-end',
                        color: 'var(--epods--color-text-primary)',
                        backgroundColor: 'var(--epods--color-bg)',

                        fontSize: 'var(--epods--font-size-p)',
                        lineHeight: 'var(--epods--line-height-p)',
                        fontFamily: 'var(--epods--font-secondary-semibold)',
                        ':hover': {
                            color: 'var(--epods--color-text-primary)',
                        },
                        ...borderStyle('none'),
                        ...paddingSize('8px', '0', '8px', '0'),
                    } as StyleObject,
                },
                ToggleIcon: {
                    style: {
                        marginRight: '4px',
                    },
                    // @ts-ignore
                    component: (props: { $expanded?: boolean }) => {
                        return (
                            <MIcon
                                size={'18px'}
                                aria-hidden="true"
                                icon={props.$expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                                className={'mr-xs'}
                            />
                        );
                    },
                },
                PanelContainer: {
                    style: ({ $theme }) => ({
                        borderWidth: '1',
                        borderColor: 'var(--epods--color-bluegrey-100)',
                    })
                }
            }}
        >
            {children}
        </StatelessAccordion>
    );
};

export const ShortcutsListView: React.FC<{
    id?: string;
    defaultGroupTitle?: string,
    viewMode?: string,
    offset?: string,
    enableSearch?: boolean,
    enableShortcutsActions?: boolean,
    children?: (args: { shortcutsList: React.ReactNode }) => any //React.ReactNode
}> = ({ id: parentId, defaultGroupTitle, children, viewMode = TView.READ, offset = '0px', enableSearch = false, enableShortcutsActions = false }) => {
    const [css] = useStyletron();

    const { state, dispatch } = useKeyboardManagerState();
    const selfRef = React.useRef<HTMLDivElement | null>(null);
    const handlePrint = useReactToPrint({
        content: () => selfRef.current,
    });

    const [conflict, setConflict] = React.useState<Conflict | null>(null);

    const shortcuts = Object.values(state.shortcuts);
    const { results, inputProps } = useFullTextSearch<KeyboardShortcutState>({
        items: Object.values(state.shortcuts),
        key: 'name',
    });
    const groups = Array.from(new Set(shortcuts.map(s => s.group || 'General')));
    const [groupedResults, setGroupedResults] = React.useState<TGroupedItem>({});
    const [savedNotification, setSavedNotification] = React.useState<string | null>(null);
    const [resetShortcut, setResetShortcut] = React.useState({ isReset: false, shortcutName: '' });
    const [resetAllShortcuts, setResetAllShortcuts] = React.useState<Boolean>(false);
    const [feedbackType, setFeedbackType] = React.useState<string | null>(null);
    React.useEffect(() => {
        const newGroupedResult = groupBy(results, i => i.item.group || 'General');
        setGroupedResults(newGroupedResult);

        setExpanded(Object.keys(newGroupedResult));
    }, [results]);

    function conflictDiscard() {
        setConflict(null);
    }

    function focusOnShortcutResetButton(id: string) {
        setTimeout(() => {
            const resetButton = document.getElementById(`input-capture-reset-${id}`);
            resetButton?.focus();
        }, 100);
    }

    function conflictResolve() {
        if (!conflict) {
            return;
        }
        focusOnShortcutResetButton(conflict.id);

        dispatch({ type: 'OVERRIDE_CONNFLICT', payload: conflict });
        // @ts-ignore
        document.dispatchEvent(
            new CustomEvent('ds-keyboard-shortcuts-override-conflict', {
                bubbles: true, // bubble event to containing elements
                composed: true, // let the event pass through the shadowDOM boundary
                detail: {
                    id: parentId,
                    payload: { conflict },
                },
            }),
        );
        setConflict(null);
    }

    const resetAllShortcutsToDefault = () => {
        dispatch({ type: 'RESET_ALL_SHORTCUT_KEYS' });
        // @ts-ignore
        document.dispatchEvent(
            new CustomEvent('ds-keyboard-shortcuts-reset-all', {
                bubbles: true, // bubble event to containing elements
                composed: true, // let the event pass through the shadowDOM boundary
                detail: {
                    id: parentId,
                    payload: {},
                },
            }),
        );
        setResetAllShortcuts(true);
        setFeedbackType('resetAllShortcuts');
    };

    const [expanded, setExpanded] = React.useState<React.Key[]>(groups.map(group => group ?? 'General'));

    const onCloseFeedback = () => {
        if (feedbackType === 'changeShortcut') {
            setSavedNotification(null);
        } else if (feedbackType === 'resetShortcut') {
            setResetShortcut(prevState => ({
                ...prevState,
                ...{ isReset: false, shortcutName: '' }
            }));
        } else if (feedbackType === 'resetAllShortcuts') {
            setResetAllShortcuts(false);
        }
        setFeedbackType(null);
    }

    const feedbackTitle = feedbackType === 'changeShortcut' ? `${savedNotification}` : (feedbackType === 'resetShortcut' ? `${resetShortcut?.shortcutName}` : undefined);
    const feedbackText = feedbackType === 'resetAllShortcuts' ? 'All keyboard shorcuts have been reset to their default values.' : (feedbackType === 'resetShortcut' ? 'has been reset.' : (feedbackType === 'changeShortcut' ? 'has been changed.' : ''));

    const shortcutsList = (
        <>
            <div className={css(shortcutsScrollableContainerStyle)}>
                {shortcuts.length > 0 ? (
                    <div className="space-y-4 keyboard-shortcuts-list">
                        <CustomStatelessAccordion expanded={expanded} setExpanded={setExpanded}>
                            {groups.map((group) => {
                                const groupTitle = group || defaultGroupTitle || 'General'

                                return <CustomPanel key={groupTitle} title={groupTitle}>
                                    <div role="list" className={css(unstyledListStyle)}>
                                        {(groupedResults[group] ?? []).map(({ item, matches }) => {
                                            const { id, name, hasConflict } = item;
                                            return (
                                                <li key={id} className="pt-xs pl-s pb-s d-block">
                                                    <div className={css(itemStyle)}>
                                                        <label className={css(labelStyle)} title={name}>
                                                            {/* @ts-ignore */}
                                                            <StringWithMatches text={name} matches={matches} />
                                                        </label>
                                                        {viewMode === TView.READ ? <div>{shortcutToString(item.keys)}</div> :
                                                            <UpdateShortcut
                                                                item={item}
                                                                setSavedNotification={setSavedNotification}
                                                                setFeedbackType={setFeedbackType}
                                                                setResetShortcut={setResetShortcut}
                                                                parentId={id}
                                                            />}
                                                    </div>
                                                    {
                                                        hasConflict ? (
                                                            <p className="h6 px-m">
                                                                <strong className="text-warning">Warning:</strong>&nbsp; This new shortcut
                                                                has been deactivated, as it conflicts with another
                                                                shortcut.
                                                            </p>
                                                        ) : null
                                                    }
                                                </li>
                                            );
                                        })}
                                    </div>
                                </CustomPanel>
                            })}
                        </CustomStatelessAccordion>
                    </div>
                ) : (
                    <p>No shortcuts</p>
                )}
                {results.length === 0 ? (
                    <p>
                        No shortcuts found with label <em>"{inputProps.value}"</em>.
                    </p>
                ) : null}
            </div>

            {
                conflict ? (
                    <>
                        <Dialog
                            // we deal with it manually
                            // @ts-ignore
                            returnFocus={false}
                            icon="warning"
                            kind="warning"
                            header={
                                conflict.type === 'KEYS' ? (
                                    <>
                                        <Shortcut>{shortcutToString(conflict.keys)}</Shortcut> already used in another active shortcut.
                                    </>
                                ) : (
                                    <>
                                        A shortcut with keys{' '}
                                        <Shortcut>{state.shortcuts[conflict.conflictingKey].keys}</Shortcut> is already
                                        active.
                                    </>
                                )
                            }
                            isOpen={true}
                            onClose={() => {
                                setConflict(null);
                            }}
                        >
                            {conflict.type === 'KEYS' ? (
                                <>
                                    <p>
                                        You can't have two active shortcuts with the same keys. The keys you recorded for{' '}
                                        <span className="font-semibold ">
                                            "{state.shortcuts[conflict.id]?.name}"
                                        </span>{' '}
                                        are already defined for{' '}
                                        <span className="font-semibold ">
                                            "{state.shortcuts[conflict.conflictingKey]?.name}"
                                        </span>
                                        .
                                    </p>
                                    <p>You can either <span className='font-semibold '>discard changes</span> or <span className='font-semibold '>disable "{state.shortcuts[conflict.conflictingKey]?.name}"</span></p>
                                </>
                            ) : (
                                <>
                                    <p>
                                        You can't have two active shortcuts with the same keys. You activated the{' '}
                                        <span className="font-semibold ">
                                            "{state.shortcuts[conflict.id]?.name}"
                                        </span>
                                        , which as the same keys as
                                        <span className="font-semibold ">
                                            "{state.shortcuts[conflict.conflictingKey]?.name}"
                                        </span>
                                        .
                                    </p>

                                    {state.shortcuts[conflict.conflictingKey]?.isDesactivatable === false ? (
                                        <>
                                            <p>
                                                As the shortcut{' '}
                                                <span className="font-semibold ">
                                                    "{state.shortcuts[conflict.conflictingKey]?.name}"
                                                </span>{' '}
                                                can't be disabled, you need{' '}
                                                <span className="font-semibold ">Discard changes</span>.
                                            </p>

                                            <p>
                                                If you want to activate this shortcut, you first need to change its keys and
                                                activate it after.
                                            </p>
                                        </>
                                    ) : (
                                        <p>
                                            You can either{' '}
                                            <span className="font-semibold ">discard changes</span> or{' '}
                                            <span className="font-semibold ">
                                                disable "{state.shortcuts[conflict.conflictingKey]?.name}"
                                            </span>
                                        </p>
                                    )}
                                </>
                            )}
                            <DialogFooter>
                                <Button onClick={conflictDiscard} theme="secondary" className="mr-s ml-auto">
                                    Discard changes
                                </Button>
                                {state.shortcuts[conflict.conflictingKey]?.isDesactivatable === false ? null : (
                                    <Button onClick={conflictResolve}>Disable conflicting shortcut</Button>
                                )}
                            </DialogFooter>
                        </Dialog>
                        <div className="fixed flex items-center justify-center top-0 left-0 right-0 bottom-0 bg-black bg-opacity-30">
                            <div className="bg-gray-50 p-2 max-w-6xl bg-white shadow-md space-y-4"></div>
                        </div>
                    </>
                ) : null}
        </>
    );

    if (children) {
        return children({ shortcutsList });
    }

    return (
        <>
            {/* <Aside ref={selfRef} className={css({ position: 'relative', top: offset })}> */}
            <Aside ref={selfRef} className={css({ position: 'relative', top: offset })}>
                {enableSearch && <div className="pb-m pb-m pt-0 px-m">
                    <h2 className="h3 mb-m mt-0">
                        Keyboard Shortcuts
                        <span className="screen-hide">{" "} - {parentId} </span>
                    </h2>
                    <div className="print-hide">
                        <Input placeholder="Search shortcut actions" aria-label="Search shortcut actions" iconBefore="search" {...inputProps} />
                    </div>
                </div>}

                {shortcutsList}

                <div className={css(shortcutActionsStyle)}>
                    {feedbackType && (
                        <KSMFeedback
                            title={feedbackTitle}
                            text={feedbackText}
                            displayTitleOnTooltip={resetAllShortcuts ? true : undefined}
                            onClose={onCloseFeedback} />
                    )}
                    {enableShortcutsActions && <div>
                        <Button className="mr-s" onClick={resetAllShortcutsToDefault} theme="tertiary" iconBefore="reset">
                            Reset all
                        </Button>
                        <Button className="" onClick={handlePrint} theme="tertiary">
                            Print
                        </Button>
                    </div>}
                </div>
            </Aside>
        </>
    );
};
